import React from 'react';
import StarSolid from 'apollo-react-icons/StarSolid';
import styles from './StyledTabs.module.css';

/**
 * Build label content for a tab (star when active + showStar, label, error indicator).
 * label can be string or React node (e.g. for badges).
 */
function buildTabLabel(tab, index, activeValue) {
  const isActive = index === activeValue;
  const showStar = tab.showStar !== false && isActive;
  return (
    <span className={styles.tabLabelContent}>
      {showStar && <StarSolid className={styles.tabStarIcon} />}
      {tab.label}
      {tab.hasError && <span className={styles.tabErrorIndicator}>!</span>}
    </span>
  );
}

/**
 * StyledTabs - Custom tab list (no MUI/apollo-react Tabs).
 *
 * Same API and look as before: value, onChange, tabs prop. Uses semantic HTML
 * (role="tablist", role="tab") and CSS that matches the previous box style,
 * avoiding apollo-react Tabs bugs (truncate loop, Windows, wrapped children).
 *
 * @param {number} props.value - Active tab index
 * @param {Function} props.onChange - (event, index) => void
 * @param {Array} props.tabs - { key?, label: string|ReactNode, showStar?, hasError? }
 * @param {string} props.variant - 'scrollable' for overflow scroll
 * @param {string} props.className - Extra class for container
 */
const StyledTabs = ({ value, onChange, tabs, variant, className }) => {
  const tabsArray = Array.isArray(tabs) && tabs.length > 0;
  const activeIndex = Number(value);
  const safeIndex =
    Number.isInteger(activeIndex) && activeIndex >= 0
      ? Math.min(activeIndex, tabsArray ? tabs.length - 1 : 0)
      : 0;

  const handleKeyDown = (e, index) => {
    if (!tabsArray) return;
    let next = index;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      next = Math.max(0, index - 1);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      next = Math.min(tabs.length - 1, index + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = tabs.length - 1;
    }
    if (next !== index) onChange?.(e, next);
  };

  const handleClick = (e, index) => {
    onChange?.(e, index);
  };

  const containerClass = [
    styles.styledTabs,
    variant === 'scrollable' && styles.scrollable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (!tabsArray) {
    return null;
  }

  return (
    <div className={containerClass} style={{ width: '100%' }}>
      <div
        className={styles.tabList}
        role="tablist"
        aria-label="Tabs"
        style={{ width: '100%' }}
      >
        {tabs.map((tab, index) => {
          const isSelected = index === safeIndex;
          const tabId = `tab-${tab.key ?? index}`;
          const panelId = `tabpanel-${tab.key ?? index}`;
          return (
            <button
              key={tab.key ?? index}
              type="button"
              role="tab"
              id={tabId}
              aria-selected={isSelected}
              aria-controls={panelId}
              tabIndex={isSelected ? 0 : -1}
              className={[styles.tab, isSelected && styles.tabSelected]
                .filter(Boolean)
                .join(' ')}
              onClick={(e) => handleClick(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              {buildTabLabel(tab, index, safeIndex)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StyledTabs;
